// Character registry + current-character resolver.
//
// All character data comes from the DB via GET /api/characters. There is no
// static fallback — if the API is unreachable the app shows an error.
//
// `getCurrentCharacter()` returns from the resolved cache (_cache) once
// loadCharacters() has completed, or null before it has. Callers that need
// the character at mount time must call resolveActiveCharacter() async and
// handle a null/loading state.
//
// Switching the active character requires a reload — AvatarModel reads
// the resolved config once in its load effect.

import { IS_LOCAL } from './devServer'
import { listCharacters as aamListCharacters, listOgsAssets } from './assetManagerApi.js'
import { hasOgsBundleMarker, hasGameableBundleMarker } from './ogsContentSelect.js'
import { reconcileCharacterUrlParam } from './characterUrlParam.js'
import { scopeKey } from './userScope'

const LS_KEY = 'aos-character-v1'

// Rewrite absolute Minio/S3 URLs to proxied API paths so the convorcher
// backend serves them with proper credentials instead of the browser
// hitting the S3 endpoint directly (which returns 403 unauthenticated).
//
// Skipped cases (URL passes through untouched):
//   1. local-assets/ URLs — served directly by the local Go server.
//   2. Any URL whose host is localhost / 127.0.0.1 — local MinIO is
//      reachable from the browser without auth, no proxy needed.
//      (When the wizard uploads to local MinIO it stores the direct
//      http://localhost:9000/splat-assets/... URL; rewriting that to
//      /api/character-assets/... breaks the load because the local Go
//      server doesn't proxy character-assets the way convorcher does.)
function rewriteS3Urls(obj) {
  if (typeof obj === 'string') {
    const m = obj.match(/^(https?:\/\/)([^/]+)\/splat-assets\/(.*)$/)
    if (!m) return obj
    const host = m[2].toLowerCase()
    // Pass through local URLs — the browser hits them directly.
    if (host.startsWith('localhost') || host.startsWith('127.0.0.1')) {
      return obj
    }
    // Strip the bucket name too — convorcher's proxy route is
    // /api/character-assets/* (no bucket prefix).
    return `/api/${m[3]}`
  }
  if (Array.isArray(obj)) return obj.map(rewriteS3Urls)
  if (obj && typeof obj === 'object') {
    const out = {}
    for (const [k, v] of Object.entries(obj)) out[k] = rewriteS3Urls(v)
    return out
  }
  return obj
}

let _cache = null
let _fetchPromise = null  // deduplicate concurrent loadCharacters() calls

export function clearCharactersCache() { _cache = null; _fetchPromise = null }

export async function loadCharacters() {
  if (_cache) return _cache
  if (_fetchPromise) return _fetchPromise

  _fetchPromise = (async () => {
    if (IS_LOCAL) {
      console.warn('%c[Characters] LOCAL server — asset-manager listCharacters', 'color:#f80;font-weight:bold')
    } else {
      console.log('[Characters] remote server — asset-manager listCharacters')
    }

    try {
      const parsed = await aamListCharacters()
      // A non-array is a malformed/failed response — fail loud (real error).
      if (!Array.isArray(parsed)) {
        throw new Error('[Characters] listCharacters returned a non-array response')
      }
      // A successful but EMPTY list is a legitimate fresh-start state, NOT an
      // error. Return [] and let the UI render the explicit empty state (the
      // "no characters → create one" screen). Per docs/UX_PRINCIPLES.md, the
      // server is the source of truth: empty means empty, not broken.
      if (parsed.length === 0) {
        console.info('[Characters] ' + (IS_LOCAL ? 'local' : 'remote') + ' server returned 0 characters (empty state)')
        _cache = []
        _fetchPromise = null
        return _cache
      }
      console.log(`[Characters] Loaded ${parsed.length} character(s): ${parsed.map(c => c.id).join(', ')}`)

      const list = parsed.map(rewriteS3Urls)

      // No draft merging here. The server is the single source of truth.
      // Any in-progress authoring draft lives in characterAuthoringStore
      // and is exposed to the UI as an explicit dirty state (with
      // backend-tag awareness) — never silently merged on top of the
      // server response. See docs/UX_PRINCIPLES.md and the Sync Model
      // section of docs/ARCHITECTURE.md.

      await enrichActiveWithPack(list)

      _cache = list
      _fetchPromise = null
      return _cache
    } catch (err) {
      _fetchPromise = null
      console.error('[Characters] ' + (IS_LOCAL ? 'local' : 'remote') + ' server connection failed:', err.message)
      console.error('[Characters] Check that the API is reachable and the server is running.')
      throw err
    }
  })()

  return _fetchPromise
}

// An OGS splat character has no rig — its inference bundle lives as loose files
// in its /ogs collection (POST /api/characters/{id}/ogs). The character API is
// the only OGS surface: detect the bundle there and, when present, mark the
// character as a texavatars splat character (so AvatarModel never mounts a
// missing rig) rendered from the loose /ogs uploads (ogsContentPacks.js).
// Server-driven, no hardcoded id list.
//
// The result is recorded ON THE CHARACTER (`renderer`) and nowhere else. Render
// mode must stay attached to the character it was probed for: a module-level
// "the active character is OGS" flag cannot say which character it describes, so
// a probe of one character can be read while a different one renders. Callers
// read it back via isSplatCharacter(character).
//
// Swallow probe failures: a transient error should leave the character on its
// normal rig path, not break the whole load (the primary list fetch above fails loud).
// ogsRows: optional pre-fetched /ogs rows (e.g. from CheckMissingRig's probe) — skips the refetch.
export async function applyCharacterPack(character, ogsRows) {
  if (!character?.id) return character
  try {
    const ogs = ogsRows ?? await listOgsAssets(character.id).catch(() => null)
    if (hasOgsBundleMarker(ogs)) character.renderer = 'texavatars'
    else if (!character.renderer && hasGameableBundleMarker(ogs)) character.renderer = 'aosrig-splat'
  } catch (e) {
    console.warn(`[Characters] OGS probe for "${character.id}" failed (treating as no bundle):`, e?.message ?? e)
  }
  return character
}

async function enrichActiveWithPack(list) {
  const id = resolveId(list)
  await applyCharacterPack(id ? list.find(c => c.id === id) : null)
}

function resolveId(list) {
  if (typeof window !== 'undefined') {
    const urlId = new URLSearchParams(window.location.search).get('character')
    if (urlId && list.some(c => c.id === urlId)) return urlId
    try {
      const lsId = localStorage.getItem(scopeKey(LS_KEY))
      if (lsId && list.some(c => c.id === lsId)) return lsId
    } catch { /* ignore */ }
  }
  return list[0]?.id
}

// Read character ID from URL params or localStorage. Does not require a loaded
// cache — reads directly from browser state. Used by loadBlueprint.js to compute
// storage keys before the async fetch completes.
export function resolveCharacterId() {
  if (typeof window !== 'undefined') {
    const urlId = new URLSearchParams(window.location.search).get('character')
    if (urlId) return urlId
    try {
      const lsId = localStorage.getItem(scopeKey(LS_KEY))
      if (lsId) return lsId
    } catch { /* ignore */ }
  }
  return null
}

// Dev override: `?ogscontent=1` forces the splat pipeline for whatever character
// is selected, skipping the /ogs bundle probe. Global by nature — it applies to
// any selection, so unlike bundle discovery (which belongs on the character, see
// applyCharacterPack) it has no character to belong to.
export function isOgsContentForced() {
  if (typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get('ogscontent') === '1'
}

// True when a character id is persisted (URL param or localStorage) but is NOT
// present in the given non-empty server list — i.e. the saved selection points
// at a character that no longer exists (deleted, backend switch, stale test
// data). Callers prompt the user to pick a valid character instead of silently
// requesting the missing one (which 404s in downstream components).
export function isStoredCharacterMissing(list) {
  if (!Array.isArray(list) || list.length === 0) return false
  const storedId = resolveCharacterId()
  if (!storedId) return false
  return !list.some(c => c.id === storedId)
}

export function getCurrentCharacterId(list) {
  if (list) return resolveId(list)
  return resolveCharacterId()
}

export function isGameableCharacter(character) {
  return character?.renderer === 'aosrig-splat' || character?.type === 'aosrig-splat'
}

// True when the character renders through the TexAvatars Gaussian-splat
// pipeline (OGSModel) instead of the rig+mesh path (AvatarModel). This is
// the canonical replacement for the legacy `?isaac=1` URL gate — selecting
// such a character from the Character menu now Just Works.
//
// Detection order:
//   1. `type === 'ogs'` — the character DECLARES it renders through this
//      pipeline. The one check that needs no per-character knowledge, so a
//      newly uploaded bundle loads with no client change: set the field and it
//      works. Every route below exists because something was missing from a row.
//   2. `renderer === 'texavatars'` — the older server field, still honoured.
//   3. slug `isaac-gs` — fallback, since the asset-manager currently strips
//      unknown fields like `renderer` on POST /api/characters.
//   4. slug `rig` — TEMPORARY, the same gap as 3: that row came up with
//      `renderer: ""` and no `type`, so nothing mounted and the console was
//      silent. Drop it once the row carries `type: 'ogs'`.
// A splat character has no rig GLB, so AvatarModel must never mount for it.
export function isSplatCharacter(character) {
  if (!character || isGameableCharacter(character)) return false
  return character.type === 'ogs'
    || character.renderer === 'texavatars'
    || character.id === 'isaac-gs'
    || character.id === 'rig'
}

/** True when `?stream=1` asks for this session to be streamed, false when
 *  `?stream=0` refuses, null when the URL says nothing.
 *
 *  STREAMING IS A WAY TO RENDER A CHARACTER, NOT A KIND OF CHARACTER. The server
 *  pulls the same `/ogs` collection the browser lifts on-device, so every OGS
 *  character can be streamed — the choice belongs to the VIEWER (their GPU, their
 *  network), not to the catalogue.
 *
 *  It used to be sayable only by publishing a SECOND character row with
 *  `renderer: 'stream'`, which is why isaac-ogs and isaac-stream are two rows for
 *  one subject. That field is gone from this decision: a duplicate row per render
 *  mode does not scale past two, it splits a character's assets, blueprints and
 *  history across rows that then drift, and it forces a catalogue edit to answer a
 *  question the viewer should answer per session. */
export function isStreamRequested(search = typeof location !== 'undefined' ? location.search : '') {
  const v = new URLSearchParams(search).get('stream')
  if (v === null) return null            // no opinion
  return v !== '0' && v !== 'false'
}

/**
 * True when THIS SESSION renders through the live OGS-streaming pipeline
 * (`StreamAvatarModel` — the server runs the inference, the browser only lifts
 * and re-shades) rather than on-device (`OGSModel`).
 *
 * TWO CONDITIONS, and both are necessary. The URL has to ask, and the character
 * has to have the `/ogs` bundle the server would stream — asking for a rig-only
 * character cannot work, and failing loudly at the URL beats a stream server
 * refusing a slug it was never going to find.
 *
 * `renderer === 'stream'` is deliberately NOT consulted. Nothing else decides a
 * render mode from the catalogue, and this one made "stream that character" mean
 * "publish another character".
 */
export function isStreamCharacter(character, search) {
  if (!character) return false
  return isStreamRequested(search) === true && isSplatCharacter(character)
}

export function setCurrentCharacterId(id) {
  let prev = null
  try { prev = localStorage.getItem(scopeKey(LS_KEY)) } catch { /* ignore */ }
  try { localStorage.setItem(scopeKey(LS_KEY), id) } catch { /* ignore */ }

  // Keep the ?character= deep-link in sync with the explicit choice. resolveId()
  // gives the URL param priority over localStorage, so a stale ?character=<old>
  // would otherwise override every in-place switch — AvatarModel re-resolves the
  // old slug and the user is pinned to it (e.g. stuck on a rigless character with
  // the "switch character" buttons doing nothing). Only rewrites a param that is
  // already present; a clean URL stays clean and localStorage drives selection.
  reconcileCharacterUrlParam(id)

  // The environment (splat) is a per-character server asset. The liveTuneStore
  // selection is a single global value persisted in localStorage, so without
  // this it would leak across characters — switching to a character with no
  // environments would still render the previous character's splat (and its
  // lighting). Clear it on a real switch so the new character starts from its
  // own (server-backed) environment list. Async import avoids a boot-time dep
  // on the UI store from this early module.
  if (prev && prev !== id) {
    import('./liveTuneStore')
      .then(({ liveTuneStore }) => {
        // assetId goes with the URL: it is written ephemerally and every
        // environment is org-wide (listEnvironments ignores its slug), so an
        // id left behind here is GUARANTEED to match a row in the next
        // character's list — which would report that environment as loaded
        // when no splat is. See resolveCurrentEnvironment.
        liveTuneStore.setState({ environment: { splatUrl: null, splatVersion: null, assetId: null } })
      })
      .catch(() => {})
  }
}

// Synchronous accessor — returns the cached character after the first
// loadCharacters() call completes, or null before that. Callers that need
// a character at initial render must use resolveActiveCharacter() and handle
// a loading state.
export function getCurrentCharacter() {
  if (!_cache) return null
  const id = resolveId(_cache)
  const base = _cache.find(c => c.id === id) ?? _cache[0]
  return base
}

// Async resolver: fetches /api/characters and returns the full config for
// the active character (including `materials` and `meshSlots`).
export async function resolveActiveCharacter() {
  const list = await loadCharacters()
  // No characters yet (fresh/empty server) — caller handles null by showing
  // the empty "create a character" state instead of a broken avatar.
  if (!list || list.length === 0) return null
  const id = resolveId(list)
  const base = list.find(c => c.id === id) ?? list[0]
  return base
}
